import React from 'react';
import PropTypes from 'prop-types';

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Helper function to return simplified flag SVGs
const getCountryFlag = (country) => {
  try {
    const upperCountry = sanitizeText(country).toUpperCase();
    
    // Changed all to includes() for flexible matching
    if (upperCountry.includes('CANADA')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#FF0000" />
          <rect x="5" y="0" width="10" height="12" fill="#FFFFFF" />
          <path d="M10,3 L9,5 L7,5 L8.5,6.5 L8,8 L10,7 L12,8 L11.5,6.5 L13,5 L11,5 Z" fill="#FF0000" />
        </svg>
      );
    } else if (upperCountry.includes('US') || upperCountry.includes('UNITED STATES')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#FFFFFF" />
          <rect x="0" y="0" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="1.8" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="3.6" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="5.4" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="7.2" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="9.0" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="10.8" width="20" height="0.9" fill="#B22234" />
          <rect x="0" y="0" width="8" height="6.5" fill="#3C3B6E" />
        </svg>
      );
    } else if (upperCountry.includes('FINLAND')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#FFFFFF" />
          <rect x="5" y="0" width="3" height="12" fill="#003580" />
          <rect x="0" y="4.5" width="20" height="3" fill="#003580" />
        </svg>
      );
    } else if (upperCountry.includes('ICELAND')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#0048E0" />
          <rect x="5" y="0" width="3" height="12" fill="#FFFFFF" />
          <rect x="0" y="4.5" width="20" height="3" fill="#FFFFFF" />
          <rect x="6" y="0" width="1" height="12" fill="#D72828" />
          <rect x="0" y="5.5" width="20" height="1" fill="#D72828" />
        </svg>
      );
    } else if (upperCountry.includes('EUROPE')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#0052B4" />
          <g fill="#FFDA44">
            <circle cx="10" cy="6" r="3.5" />
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30) * Math.PI / 180;
              const x = 10 + 4.5 * Math.sin(angle);
              const y = 6 - 4.5 * Math.cos(angle);
              return <circle key={i} cx={x} cy={y} r="0.7" />;
            })}
          </g>
        </svg>
      );
    } else if (upperCountry.includes('ASIA') || upperCountry.includes('JAPAN')) {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#FFFFFF" />
          <circle cx="10" cy="6" r="3.5" fill="#BC002D" />
        </svg>
      );
    } else {
      return (
        <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="20" height="12" fill="#444444" />
        </svg>
      );
    }
  } catch (error) {
    return (
      <svg viewBox="0 0 20 12" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="20" height="12" fill="#444444" />
      </svg>
    );
  }
};

const LocationSelector = ({ locations = [], selectedLocation = '', onSelectLocation, isLoading = false }) => {
  
  // Handle location selection
  const handleLocationSelect = (locationId) => {
    if (typeof onSelectLocation === 'function') {
      onSelectLocation(locationId);
    }
  };
  
  // Loading indicator component
  const LoadingIndicator = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-2">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="py-4 md:py-3 px-3 rounded-md h-[60px] md:h-[52px] bg-[#1d1d1d] animate-pulse"></div>
      ))}
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="col-span-2 md:col-span-3 text-center text-gray-300 text-sm py-8 md:py-6 min-h-[120px] md:min-h-[100px] flex items-center justify-center">
      No locations available
    </div>
  );
  
  const hasLocations = Array.isArray(locations) && locations.length > 0;
  
  return (
    <div className="bg-[#121212] p-4 sm:p-3 md:p-1.5 rounded-md mb-4 md:mb-1.5 w-full md:w-[90%] md:mx-auto min-h-[180px] sm:min-h-[170px] md:min-h-[160px]">
      <h3 className="font-semibold text-white mb-4 sm:mb-3 md:mb-1 text-base sm:text-sm md:text-xs">Location</h3>
      
      {/* Responsive grid layout */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-2">
        {isLoading ? (
          <LoadingIndicator />
        ) : !hasLocations ? (
          <EmptyState />
        ) : (
          locations.map((location) => {
            if (!location || !location.id || !location.name) {
              return null;
            }
            
            // For flag matching - extract just country name (first word)
            const countryForFlag = sanitizeText(location.name.split(/[-\s]/)[0]);
            
            // For display - show full name with number, formatted nicely
            const displayName = sanitizeText(location.name)
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            
            return (
              <div
                key={location.id}
                className={`relative py-4 sm:py-4 md:py-3 px-3 flex items-center justify-center rounded-md transition-all cursor-pointer h-[60px] sm:h-[56px] md:h-[52px] min-h-[60px] sm:min-h-[56px] md:min-h-[52px] ${
                  selectedLocation === location.id
                    ? 'bg-[#1e2428] border border-[#e0eacc]'
                    : 'bg-[#1d1d1d] border border-[#333333] hover:border-gray-500'
                }`}
                onClick={() => handleLocationSelect(location.id)}
                role="button"
                tabIndex={0}
                aria-pressed={selectedLocation === location.id}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLocationSelect(location.id);
                  }
                }}
              >
                {/* Display flag and location name */}
                <div className="flex items-center space-x-2">
                  {/* Flag - responsive size */}
                  <div className="w-6 h-5 sm:w-5 sm:h-4 md:w-4 md:h-3 flex-shrink-0">
                    {getCountryFlag(countryForFlag)}
                  </div>
                  
                  {/* Location name with number */}
                  <span
                    className={`text-sm sm:text-sm md:text-xs font-medium text-center ${
                      selectedLocation === location.id
                        ? 'text-[#e0eacc]'
                        : 'text-gray-300'
                    }`}
                  >
                    {displayName}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// PropTypes for type validation
LocationSelector.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  selectedLocation: PropTypes.string,
  onSelectLocation: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

const LocationSelectorComponent = React.memo(LocationSelector);

export default LocationSelectorComponent;